import * as React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  TextField,
  InputAdornment,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Chip,
  Stack,
} from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { useNavigate } from "react-router-dom";

import { listPatients } from "../api/patientsApi";
import { listHospitals } from "../api/hospitalsApi";
import { Colors } from "../../utils/Colors";
import { ROUTES } from "../../shared/constants/routes";

const API_BASE = "http://127.0.0.1:8000/api";

async function parseJsonSafe(response) {
  const text = await response.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return {};
  }
}

function getHeaders() {
  const accessToken = localStorage.getItem("access_token");
  const token = localStorage.getItem("token");

  return {
    "Content-Type": "application/json",
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    ...(token ? { Authorization: `Token ${token}` } : {}),
  };
}

async function fetchList(url) {
  const res = await fetch(url, { headers: getHeaders() });
  const data = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(data?.detail || `Failed to load ${url}`);
  }
  return Array.isArray(data) ? data : data?.results || [];
}

function makeMap(rows, idKeys, nameKeys) {
  const map = {};
  rows.forEach((row) => {
    const id = idKeys.map((k) => row?.[k]).find((v) => v !== undefined && v !== null && v !== "");
    const name = nameKeys.map((k) => row?.[k]).find((v) => v !== undefined && v !== null && v !== "");
    if (id !== undefined && id !== null && id !== "") {
      map[String(id)] = name || `ID ${id}`;
    }
  });
  return map;
}

function getPatientId(p) {
  return (
    p?.patient_id ??
    p?.id ??
    p?.pk ??
    null
  );
}

function getHospitalIdFromAny(patient, admission) {
  return (
    admission?.organization_id ??
    admission?.organization ??
    admission?.hospital_id ??
    admission?.hospital ??
    patient?.organization_id ??
    patient?.organization?.organization_id ??
    patient?.organization?.id ??
    patient?.hospital_id ??
    patient?.hospital?.hospital_id ??
    patient?.hospital?.id ??
    patient?.organization ??
    patient?.hospital ??
    null
  );
}

function findLatestAdmissionForPatient(patient, admissions) {
  const pid = String(getPatientId(patient) ?? "");
  const uhid = String(patient?.uhid ?? "");

  const matches = admissions.filter((a) => {
    return (
      String(a?.patient ?? "") === pid ||
      String(a?.patient_id ?? "") === pid ||
      String(a?.patient?.id ?? "") === pid ||
      String(a?.patient?.patient_id ?? "") === pid ||
      String(a?.uhid ?? "") === uhid
    );
  });

  if (!matches.length) return null;

  return [...matches].sort((a, b) => {
    const ta = new Date(a?.admit_at || a?.created_at || 0).getTime();
    const tb = new Date(b?.admit_at || b?.created_at || 0).getTime();
    return tb - ta;
  })[0];
}

function fmt(v) {
  if (v === undefined || v === null || v === "") return "—";
  return String(v);
}

function fmtDate(v) {
  if (!v) return "—";
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toLocaleString();
  } catch {
    return String(v);
  }
}

export default function PatientListPage() {
  const nav = useNavigate();

  const [q, setQ] = React.useState("");
  const [rows, setRows] = React.useState([]);
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  async function load() {
    setLoading(true);
    setError("");

    try {
      const [
        patients,
        hospitals,
        admissions,
        departments,
        wards,
        rooms,
        beds,
        users,
      ] = await Promise.all([
        listPatients(),
        listHospitals(),
        fetchList(`${API_BASE}/patients/admissions/?format=json`),
        fetchList(`${API_BASE}/departments/?format=json`),
        fetchList(`${API_BASE}/wards/?format=json`),
        fetchList(`${API_BASE}/rooms/?format=json`),
        fetchList(`${API_BASE}/beds/?format=json`),
        fetchList(`${API_BASE}/users/?format=json`),
      ]);

      const patientRows = Array.isArray(patients) ? patients : patients?.results || [];
      const hospitalRows = Array.isArray(hospitals) ? hospitals : hospitals?.results || [];

      const hospitalMap = makeMap(
        hospitalRows,
        ["organization_id", "hospital_id", "id", "uuid"],
        ["name", "organization_name", "hospital_name", "title"]
      );

      const departmentMap = makeMap(
        departments,
        ["department_id", "id"],
        ["department_name", "name", "title"]
      );

      const wardMap = makeMap(
        wards,
        ["ward_id", "id"],
        ["ward_name", "name", "unit_name", "title"]
      );

      const roomMap = makeMap(
        rooms,
        ["room_id", "id"],
        ["room_number", "room_name", "name", "title"]
      );

      const bedMap = makeMap(
        beds,
        ["bed_id", "id"],
        ["bed_number", "bed_name", "name", "title"]
      );

      const userMap = makeMap(
        users,
        ["user_id", "id"],
        ["full_name", "name", "username", "email"]
      );

      const merged = patientRows.map((p) => {
        const admission = findLatestAdmissionForPatient(p, admissions);

        const hospitalId = getHospitalIdFromAny(p, admission);

        const hospitalName =
          p?.organization_name ||
          p?.hospital_name ||
          p?.organization?.name ||
          p?.organization?.organization_name ||
          p?.hospital?.name ||
          p?.hospital?.organization_name ||
          admission?.organization_name ||
          admission?.hospital_name ||
          admission?.organization?.name ||
          admission?.hospital?.name ||
          hospitalMap[String(hospitalId)] ||
          "—";

        const departmentName =
          admission?.department_name ||
          departmentMap[String(admission?.department_id ?? admission?.department ?? "")] ||
          admission?.department ||
          "—";

        const wardName =
          admission?.ward_name ||
          admission?.ward_unit ||
          wardMap[String(admission?.ward_id ?? admission?.ward ?? "")] ||
          "—";

        const roomName =
          admission?.room_name ||
          admission?.room_number ||
          roomMap[String(admission?.room_id ?? admission?.room ?? "")] ||
          "—";

        const bedName =
          admission?.bed_name ||
          admission?.bed_number ||
          bedMap[String(admission?.bed_id ?? admission?.bed ?? "")] ||
          "—";

        const doctorName =
          admission?.treating_doctor_name ||
          admission?.attending_doctor_name ||
          admission?.doctor_name ||
          userMap[String(admission?.attending_doctor ?? admission?.attending_doctor_id ?? "")] ||
          admission?.treating_doctor ||
          "—";

        const consultantName =
          admission?.consultant_name ||
          userMap[String(admission?.consultant ?? admission?.consultant_id ?? "")] ||
          admission?.consultant ||
          "—";

        return {
          ...p,
          latestAdmission: admission,
          hospitalName,
          departmentName,
          wardName,
          roomName,
          bedName,
          doctorName,
          consultantName,
        };
      });

      setRows(merged);
    } catch (e) {
      setError(e?.message || "Failed to load patient details");
    } finally {
      setLoading(false);
    }
  }

  React.useEffect(() => {
    load();
  }, []);

  const filtered = React.useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;

    return rows.filter((p) => {
      return [
        p?.hospitalName,
        p?.uhid,
        p?.full_name,
        p?.primary_phone,
        p?.departmentName,
        p?.wardName,
        p?.roomName,
        p?.bedName,
        p?.doctorName,
        p?.consultantName,
      ]
        .map((v) => String(v ?? "").toLowerCase())
        .some((v) => v.includes(s));
    });
  }, [q, rows]);

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, background: Colors.bg.app, minHeight: "100%" }}>
      <Box sx={{ maxWidth: 1600, mx: "auto" }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={1.5}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 900, color: Colors.text.title }}>
              Patients
            </Typography>
            <Typography variant="body2" sx={{ color: Colors.text.muted }}>
              Search patient and view hospital, admission, ward, bed and doctor details.
            </Typography>
          </Box>

          <Chip label={loading ? "Loading..." : `${filtered.length} records`} variant="outlined" />
        </Stack>

        {error ? (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
            <Typography color="error">{error}</Typography>
          </Paper>
        ) : null}

        <Card
          elevation={0}
          sx={{
            border: `1px solid ${Colors.border.light}`,
            borderRadius: 3,
            boxShadow: Colors.shadow.brand,
            background: Colors.bg.paper,
          }}
        >
          <CardContent>
            <TextField
              fullWidth
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by hospital / UHID / patient / department / doctor / bed"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlinedIcon />
                  </InputAdornment>
                ),
              }}
            />

            <Divider sx={{ my: 2 }} />

            <Paper variant="outlined" sx={{ borderRadius: 2, overflowX: "auto" }}>
              <Table size="small">
                <TableHead sx={{ background: Colors.brand.accentBar }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 900 }}>Hospital</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Full Name</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Age</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Gender</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>UHID</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Mobile</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Address</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Emergency Name</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Relationship</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Emergency Number</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Admission Date/Time</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Department</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Ward / Unit</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Room</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Bed Number</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Treating Doctor</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Consultant</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filtered.map((p) => (
                    <TableRow key={String(p?.id ?? p?.patient_id ?? p?.uhid)} hover>
                      <TableCell>{fmt(p.hospitalName)}</TableCell>

                      <TableCell
                        sx={{ cursor: "pointer" }}
                        onClick={() => nav(ROUTES.PATIENT_PROFILE(p.uhid))}
                      >
                        <Typography sx={{ fontWeight: 800, color: Colors.text.body }}>
                          {fmt(p.full_name)}
                        </Typography>
                      </TableCell>

                      <TableCell>{fmt(p.age)}</TableCell>
                      <TableCell>{fmt(p.sex ?? p.gender)}</TableCell>

                      <TableCell
                        sx={{ color: Colors.brand.primary, fontWeight: 900, cursor: "pointer" }}
                        onClick={() => nav(ROUTES.PATIENT_PROFILE(p.uhid))}
                      >
                        {fmt(p.uhid)}
                      </TableCell>

                      <TableCell>{fmt(p.primary_phone ?? p.phone)}</TableCell>
                      <TableCell>{fmt(p.address)}</TableCell>
                      <TableCell>{fmt(p.emergency_contact_name)}</TableCell>
                      <TableCell>{fmt(p.emergency_contact_relation ?? p.emergency_relationship)}</TableCell>
                      <TableCell>{fmt(p.emergency_contact_phone)}</TableCell>
                      <TableCell>{fmtDate(p?.latestAdmission?.admit_at)}</TableCell>
                      <TableCell>{fmt(p.departmentName)}</TableCell>
                      <TableCell>{fmt(p.wardName)}</TableCell>
                      <TableCell>{fmt(p.roomName)}</TableCell>
                      <TableCell>{fmt(p.bedName)}</TableCell>
                      <TableCell>{fmt(p.doctorName)}</TableCell>
                      <TableCell>{fmt(p.consultantName)}</TableCell>
                    </TableRow>
                  ))}

                  {!filtered.length ? (
                    <TableRow>
                      <TableCell colSpan={17} sx={{ py: 4, textAlign: "center", color: Colors.text.muted }}>
                        No patients found.
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </Paper>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}